import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import hostelService from '../../services/hostelService';
import studentsService from '../../services/studentsService';
import { FormSelect } from '../../components/finance';
import { Plus, MagnifyingGlass, PencilSimple, Trash, CircleNotch, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function StudentAllocationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [filteredAllocations, setFilteredAllocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
    hostel_id: '',
    room_id: '',
    check_in_date: '',
    academic_year: '2025/2026',
    status: 'active'
  });
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAllocations();
  }, [allocations, searchTerm, statusFilter, filterAllocations]);

  const loadData = async () => {
    try {
      setLoading(true);
      const allocResponse = hostelService.listAccommodation().catch(() => []);
      const studentsResponse = studentsService.listStudents().catch(err => {
        console.error('Students API error:', err);
        return [];
      });
      const hostelsResponse = hostelService.listHostels().catch(() => []);
      const roomsResponse = hostelService.listRooms().catch(() => []);

      const [allocData, studentsData, hostelsData, roomsData] = await Promise.all([
        allocResponse,
        studentsResponse,
        hostelsResponse,
        roomsResponse
      ]);

      const allocList = Array.isArray(allocData) ? allocData : allocData.allocations || [];
      const studentsList = Array.isArray(studentsData) ? studentsData : studentsData.students || studentsData.items || [];
      const hostelsList = Array.isArray(hostelsData) ? hostelsData : hostelsData.hostels || [];
      const roomsList = Array.isArray(roomsData) ? roomsData : roomsData.rooms || [];

      setAllocations(allocList);
      setStudents(studentsList);
      setHostels(hostelsList);
      setRooms(roomsList);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load allocations');
    } finally {
      setLoading(false);
    }
  };

  const filterAllocations = React.useCallback(() => {
    let filtered = allocations;

    if (searchTerm) {
      filtered = filtered.filter(a => {
        const student = students.find(s => s.student_id === a.student_id);
        return student?.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               student?.last_name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    setFilteredAllocations(filtered);
  }, [allocations, searchTerm, statusFilter, students]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await hostelService.updateAccommodation(editingId, formData);
        toast.success('Allocation updated successfully');
      } else {
        await hostelService.createAccommodation(formData);
        toast.success('Allocation created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      loadData();
    } catch (error) {
      console.error('Error saving allocation:', error);
      toast.error(editingId ? 'Failed to update allocation' : 'Failed to create allocation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await hostelService.deleteAccommodation(deleteTarget);
      toast.success('Allocation deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting allocation:', error);
      toast.error('Failed to delete allocation');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.student_id} - ${student.first_name} ${student.last_name}` : 'Unknown';
  };

  const getHostelName = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    return hostel ? hostel.hostel_name : 'Unknown';
  };

  const getRoomNumber = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.room_number : 'Unknown';
  };

  if (loading) {
    return <Layout><div className="loading">Loading allocations...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Student Allocations</h1>
              <p>Allocate students to hostel rooms</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <Plus size={20} />
            Add Allocation
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {showForm && (
          <div className="form-container">
            <div className="form-card">
              <h2>Add Student Allocation</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <FormSelect
                    label="Student"
                    name="student_id"
                    value={formData.student_id}
                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    required
                    disabled={submitting}
                    options={students.map(s => ({
                      value: s.student_id,
                      label: `${s.student_id} - ${s.first_name} ${s.last_name}`
                    }))}
                    placeholder="Select Student"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Hostel"
                      name="hostel_id"
                      value={formData.hostel_id}
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                      required
                      disabled={submitting}
                      options={hostels.map(h => ({
                        value: h.id,
                        label: h.hostel_name
                      }))}
                      placeholder="Select Hostel"
                    />
                  </div>

                  <div className="form-group">
                    <FormSelect
                      label="Room"
                      name="room_id"
                      value={formData.room_id}
                      onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                      required
                      disabled={submitting}
                      options={(formData.hostel_id 
                        ? rooms.filter(r => r.hostel_id === formData.hostel_id)
                        : rooms).map(r => ({
                        value: r.id,
                        label: r.room_number
                      }))}
                      placeholder="Select Room"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in Date *</label>
                    <input
                      type="date"
                      value={formData.check_in_date}
                      onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group">
                    <label>Academic Year *</label>
                    <input
                      type="text"
                      value={formData.academic_year}
                      onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                      required
                      disabled={submitting}
                      placeholder="e.g., 2025/2026"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <FormSelect
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                    disabled={submitting}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : 'Create Allocation'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={submitting}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table-container">
          {filteredAllocations.length === 0 ? (
            <div className="empty-state">
              <p>No allocations found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Hostel</th>
                  <th>Room</th>
                  <th>Check-in Date</th>
                  <th>Academic Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAllocations.map((alloc) => (
                  <tr key={alloc.id}>
                    <td>{getStudentName(alloc.student_id)}</td>
                    <td>{getHostelName(alloc.hostel_id)}</td>
                    <td>{getRoomNumber(alloc.room_id)}</td>
                    <td>{alloc.check_in_date}</td>
                    <td>{alloc.academic_year}</td>
                    <td>
                      <span className={`badge badge-${alloc.status === 'active' ? 'success' : 'secondary'}`}>
                        {alloc.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(alloc.id)}
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default StudentAllocationsPage;
